<script setup>
/**
 * O SELETOR DE ETIQUETAS do produto — o "+ Etiqueta" e a lista que abre.
 *
 * 🔴 POR QUE EXISTE (16/09/2026, relato do Victor: "estava tentando
 * adicionar uma etiqueta e não conseguia").
 * A lista antiga era `absolute` dentro da ficha do contato, que rola e
 * recorta. Na aba CRM ela abria perto do fim da tela (y=755 numa tela de
 * 820) e só o campo de busca aparecia: as etiquetas ficavam escondidas
 * abaixo da borda, e ainda passavam 8px à direita. Gravar funcionava — não
 * dava era para ver onde clicar.
 *
 * Agora a lista vive no `body`, presa à tela: abre para cima quando não há
 * espaço embaixo e nunca passa da borda. Uma peça só para a etiqueta do
 * CONTATO e a da CONVERSA.
 */
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';

const props = defineProps({
  // [{ id, title, color }]
  etiquetas: { type: Array, default: () => [] },
  // títulos já marcados
  escolhidas: { type: Array, default: () => [] },
  // Rótulo da lista para o leitor de tela ("Etiquetas do contato"…).
  rotulo: { type: String, default: '' },
  // Admin cria a etiqueta que não achou, sem sair da conversa.
  podeCriar: { type: Boolean, default: false },
});

const emit = defineEmits(['alternar', 'criar']);
const { t } = useI18n();

const aberto = ref(false);
const busca = ref('');
const gatilho = ref(null);
const lista = ref(null);
const campo = ref(null);
const estilo = ref({});

const ALTURA_MAXIMA = 300;
const MARGEM = 8;

const nomeVisivel = titulo => titulo.replace(/-/g, ' ').toUpperCase();

const filtradas = computed(() => {
  const termo = busca.value.trim().toLowerCase().replace(/\s+/g, '-');
  const base = termo
    ? props.etiquetas.filter(e => e.title.toLowerCase().includes(termo))
    : props.etiquetas;
  // As marcadas primeiro: é o que a pessoa confere antes de mexer.
  return base.toSorted(
    (a, b) =>
      Number(props.escolhidas.includes(b.title)) -
      Number(props.escolhidas.includes(a.title))
  );
});

const termoParaCriar = computed(() =>
  busca.value.trim().toLowerCase().replace(/\s+/g, '-')
);
const mostraCriar = computed(
  () =>
    props.podeCriar &&
    termoParaCriar.value &&
    !props.etiquetas.some(e => e.title === termoParaCriar.value)
);

const posicionar = () => {
  const r = gatilho.value?.getBoundingClientRect();
  if (!r) return;
  // offsetWidth ignora a escala da animação de entrada.
  const largura = lista.value?.offsetWidth || 240;
  const paraCima = window.innerHeight - r.bottom < ALTURA_MAXIMA + MARGEM;
  const esquerda = Math.max(
    MARGEM,
    Math.min(r.left, window.innerWidth - largura - MARGEM)
  );
  estilo.value = {
    left: `${esquerda}px`,
    ...(paraCima
      ? { bottom: `${window.innerHeight - r.top + 4}px` }
      : { top: `${r.bottom + 4}px` }),
  };
};

// As escutas de rolagem e redimensionamento vivem enquanto a lista está
// aberta; um só `abort()` desliga as duas.
let escutas = null;

const fechar = () => {
  aberto.value = false;
  busca.value = '';
  escutas?.abort();
  escutas = null;
};

const abrir = async () => {
  aberto.value = true;
  await nextTick();
  posicionar();
  campo.value?.focus();
  escutas = new AbortController();
  const { signal } = escutas;
  // Rolar a PÁGINA com a lista aberta deixaria ela longe do botão; rolar
  // DENTRO da lista é só ler as opções.
  window.addEventListener(
    'scroll',
    evento => {
      if (evento.target instanceof Node && lista.value?.contains(evento.target))
        return;
      fechar();
    },
    { capture: true, signal }
  );
  window.addEventListener('resize', fechar, { signal });
};

// O chip novo entra antes do botão e pode empurrá-lo: a lista acompanha.
watch(
  () => props.escolhidas,
  async () => {
    if (!aberto.value) return;
    await nextTick();
    posicionar();
  }
);

const alternarLista = () => (aberto.value ? fechar() : abrir());

const criar = () => {
  emit('criar', termoParaCriar.value);
  fechar();
};

const cliqueFora = [fechar, { ignore: [gatilho] }];

// O atalho "L" da conversa abre e fecha a lista.
defineExpose({ alternarLista });

onBeforeUnmount(fechar);
</script>

<template>
  <button
    ref="gatilho"
    type="button"
    class="sde-gatilho"
    :class="{ 'sde-gatilho--aberto': aberto }"
    :aria-expanded="aberto"
    aria-haspopup="listbox"
    @click="alternarLista"
  >
    <i class="i-lucide-plus" aria-hidden="true" />
    {{ t('LABEL.TAG_BUTTON') }}
  </button>

  <Teleport to="body">
    <Transition name="advup-pop">
      <div
        v-if="aberto"
        ref="lista"
        v-on-click-outside="cliqueFora"
        class="advup-menu advup-pop sde-lista"
        :style="estilo"
        @keydown.esc="fechar"
      >
        <label class="advup-busca advup-busca--bloco sde-busca">
          <i class="i-lucide-search advup-busca__lupa" aria-hidden="true" />
          <input
            ref="campo"
            v-model="busca"
            type="search"
            class="advup-busca__campo reset-base"
            :placeholder="t('LABEL.SEARCH')"
            :aria-label="t('LABEL.SEARCH')"
          />
        </label>
        <ul
          class="sde-itens"
          role="listbox"
          aria-multiselectable="true"
          :aria-label="rotulo || t('LABEL.TAG_BUTTON')"
        >
          <li v-for="etiqueta in filtradas" :key="etiqueta.id">
            <button
              type="button"
              role="option"
              class="advup-menu__item sde-item"
              :class="{ 'sde-item--on': escolhidas.includes(etiqueta.title) }"
              :aria-selected="escolhidas.includes(etiqueta.title)"
              @click="emit('alternar', etiqueta)"
            >
              <span
                class="sde-ponto"
                :style="{ '--etq': etiqueta.color }"
                aria-hidden="true"
              />
              <span class="sde-nome">{{ nomeVisivel(etiqueta.title) }}</span>
              <i
                v-if="escolhidas.includes(etiqueta.title)"
                class="i-lucide-check sde-visto"
                aria-hidden="true"
              />
            </button>
          </li>
          <li v-if="!filtradas.length" class="advup-nota sde-nada">
            {{ t('LABEL.NONE_FOUND') }}
          </li>
        </ul>
        <button
          v-if="mostraCriar"
          type="button"
          class="advup-menu__item sde-criar"
          @click="criar"
        >
          <i class="i-lucide-plus" aria-hidden="true" />
          <span class="sde-nome">
            {{ t('LABEL.CREATE', { nome: nomeVisivel(termoParaCriar) }) }}
          </span>
        </button>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.sde-gatilho {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-1);
  height: 24px;
  padding: 0 var(--esp-2);
  border: 1px dashed rgb(var(--slate-7));
  border-radius: var(--r-redondo);
  background: transparent;
  font-size: var(--txt-rotulo);
  font-weight: var(--peso-rotulo);
  letter-spacing: var(--rastro-rotulo);
  text-transform: uppercase;
  color: rgb(var(--slate-11));
  cursor: pointer;
  transition:
    border-color var(--mov-1) var(--mov-curva),
    background-color var(--mov-1) var(--mov-curva),
    color var(--mov-1) var(--mov-curva);
}

.sde-gatilho > i {
  width: 12px;
  height: 12px;
  transition: rotate var(--mov-2) var(--mov-curva-pulo);
}

.sde-gatilho:hover,
.sde-gatilho--aberto {
  border-style: solid;
  border-color: rgb(var(--blue-7));
  background: rgb(var(--blue-2));
  color: rgb(var(--blue-11));
}

.sde-gatilho--aberto > i {
  rotate: 45deg;
}

.sde-gatilho:focus-visible {
  outline: 2px solid rgb(var(--blue-9));
  outline-offset: 2px;
}

.sde-lista {
  position: fixed;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  width: max-content;
  min-width: 15rem;
  max-width: min(22rem, calc(100vw - 16px));
  max-height: 300px;
}

.sde-busca {
  flex-shrink: 0;
  margin: var(--esp-1) var(--esp-1) var(--esp-1);
}

.sde-itens {
  min-height: 0;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  list-style: none;
}

.sde-item {
  font-size: var(--txt-nota);
  font-weight: var(--peso-forte);
  letter-spacing: var(--rastro-rotulo);
}

.sde-item--on {
  color: rgb(var(--blue-11));
}

.sde-ponto {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: var(--r-redondo);
  background: var(--etq);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--etq) 25%, transparent);
}

.sde-nome {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sde-visto {
  flex-shrink: 0;
  font-size: var(--ico-2);
  animation: advupPop var(--mov-2) var(--mov-curva-pulo) backwards;
}

.sde-criar {
  flex-shrink: 0;
  border-top: 1px solid rgb(var(--slate-5));
  border-radius: 0;
  color: rgb(var(--blue-11));
  font-size: var(--txt-nota);
  font-weight: var(--peso-forte);
}

.sde-nada {
  margin: 0;
  padding: var(--esp-3);
  text-align: center;
}

@media (prefers-reduced-motion: reduce) {
  .sde-gatilho,
  .sde-gatilho > i {
    transition: none;
  }

  .sde-visto {
    animation: none;
  }
}
</style>
